<template>
  <Background
    v-if="displayBlock"
    :background="{ theme: 'black' }"
    class="overflow-hidden"
    rounded="always"
  >
    <div class="w-full md:min-h-[516px] content-center">
      <section class="flex">
        <div
          class="flex-col flex md:flex-row gap-24 relative w-full justify-between"
          :class="{
            'px-24 pb-24 md:p-24 z-[1]': image,
            'p-24': !image,
          }"
        >
          <div
            class="flex flex-col md:pt-0 w-full"
            :class="{
              'pt-[224px] lg:justify-center lg:w-[416px]': image,
              'md:justify-center max-w-[580px]': !image,
            }"
          >
            <ImageRenderer
              v-if="image"
              :alt="image?.alt"
              class="h-[200px] absolute md:static left-0 right-0 top-0 w-full lg:hidden md:mx-0 md:mt-0 md:rounded-lg overflow-hidden mb-24"
              :height="image?.heightMobile || 0"
              :height-mobile="image?.heightMobile"
              image-class="h-full"
              :src="image?.srcMobile"
              :src-mobile="image?.srcMobile"
              :width="image?.widthMobile || 0"
              :width-mobile="image?.widthMobile"
            />

            <div :class="{ 'md:max-w-[580px]': image }">
              <RevTag
                v-if="tag"
                class="mood-main w-fit mb-8"
                :label="tag"
                size="large"
                variant="primary"
              />

              <p v-if="eyebrowText && !tag" class="mb-8 body-2">
                {{ eyebrowText }}
              </p>

              <RichText
                class="punchline hyphens-auto mb-8 md:mb-20"
                :node-renderers-overrides="renderParagraphAsHeading"
                :rich-text="title"
                style-variant="none"
              />

              <RichText
                v-if="subtitle"
                class="body-2 md:heading-3 mb-24 md:mb-32 hyphens-auto"
                :rich-text="subtitle"
                style-variant="none"
              />

              <component
                :is="ctaList.length > 1 ? 'ul' : 'div'"
                v-if="ctaList && ctaList.length"
                ref="spotlight-cta-scroller"
                class="flex gap-10 lg:items-center -mr-24 flex-nowrap overflow-x-auto scrollbar-none pr-24 lg:mr-0 lg:flex-wrap lg:pr-0"
                :class="maskClass"
                data-test="spotlight-cta-wrapper"
              >
                <ProductSpotlightCTA
                  v-for="(cta, index) in ctaList"
                  :key="`${cta.label}-${index}`"
                  :ariaLabelledBy="titleId"
                  :multiple="ctaList.length > 1"
                  v-bind="cta"
                  :tracking="{ ...tracking, ctaPosition: index + 1 }"
                />
              </component>
            </div>
          </div>

          <ProductCardCarouselCard
            v-if="spotlightProduct"
            class="mood-main text-static-default-hi w-full !h-fit md:max-w-256"
            :index="0"
            :product="spotlightProduct"
            :product-granularity="productGranularity ?? 'variant'"
            :tracking="{
              ...tracking,
              productComponent: 'ProductSpotlight',
              list: trackingList,
            }"
          />
        </div>

        <ImageRenderer
          v-if="image"
          :alt="image?.alt"
          class="max-w-[920px] w-full hidden lg:block absolute right-0 top-0 bottom-0"
          :class="$style.imageGradient"
          :height="image?.height"
          :height-mobile="image?.heightMobile"
          image-class="h-full"
          :src="image?.src"
          :src-mobile="image?.srcMobile"
          :width="image?.width"
          :width-mobile="image?.widthMobile"
        />
      </section>
    </div>
  </Background>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, h, useTemplateRef } from 'vue'

import type { ProductSpotlightApiProps } from '@backmarket/http-api/src/api-specs-content/models/product-spotlight'
import { useDiscountedPrice } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useDiscountedPrice'
import ProductCardCarouselCard from '@backmarket/nuxt-layer-recommendation/ProductCardCarouselCard.vue'
import { useAddProductsCacheTagHeader } from '@backmarket/nuxt-module-http-cache/composables/useAddCacheTagHeader'
import { tw } from '@backmarket/utils/string/tw'
import { RevTag } from '@ds/components/Tag'
import type { NodeRendererMap } from 'contentful-rich-text-vue-renderer'

import { useScrollMaskClass } from '../../composables/useScrollMaskClass'
import type { BlockProps } from '../../models/content-block'
import { useSearchProducts } from '../../products/useSearchProducts'
import Background from '../../shared-components/Background/Background.vue'
import ImageRenderer from '../../shared-components/ImageRenderer/ImageRenderer.vue'
import RichText from '../RichText/RichText.vue'
import { findTitleId } from '../RichText/RichTextVueParser.utils'

import ProductSpotlightCTA from './ProductSpotlightCTA.vue'

export type ProductSpotlightBlockProps = ProductSpotlightApiProps & BlockProps

// The v1 of this block had a background prop.
// Vue's prop fallthrough rules would override the explicitly declared "background"
// prop in <Background> with the fallthrough value returned by the API if the page cache has it.
// -> This way we ignore any fallthrough values for this component.
// TODO: remove this once the back-end API is updated and all the cache is expired for CMS pages (~1 month)
defineOptions({ inheritAttrs: false })

const {
  eyebrowText,
  tag,
  title,
  subtitle,
  productFilters,
  productGranularity,
  ctaList,
  tracking,
  image,
} = defineProps<ProductSpotlightBlockProps>()

const titleId = findTitleId(title)

const productCategoriesScrollerRef = useTemplateRef<HTMLDivElement>(
  'spotlight-cta-scroller',
)
const { maskClass } = useScrollMaskClass(productCategoriesScrollerRef)
const route = useRoute()

const { data: spotlightProducts, pending } = useSearchProducts(
  { productQuery: productFilters },
  { immediate: true, server: true },
)
const { products: parsedSpotlightProducts } =
  useDiscountedPrice(spotlightProducts)

useAddProductsCacheTagHeader({ products: spotlightProducts })

const spotlightProduct = computed(() => {
  if (!parsedSpotlightProducts.value) return undefined

  return parsedSpotlightProducts.value[0]
})

const displayBlock = computed(() => {
  if (pending.value || !spotlightProduct.value) return false
  if (!spotlightProduct.value.availableStock) return false

  return true
})

const trackingList = computed(() => {
  const { name, params } = route
  const pageName = params?.pageName ?? name

  return String(tracking?.zone ?? pageName)
})

// render the title as a h2 tag
const renderParagraphAsHeading: NodeRendererMap = {
  paragraph: (node, key, next) => {
    return h(
      'h2',
      { key, class: tw`empty:hidden`, id: titleId },
      next(node.content, key, next),
    )
  },
}
</script>

<style module>
.imageGradient {
  mask-image: linear-gradient(
    90deg,
    transparent,
    rgba(0, 0, 0, 1) 36%,
    rgba(0, 0, 0, 1) 76%,
    transparent
  );
}
</style>
